// eslint-disable-next-line @compiled/jsx-pragma
/** @jsxImportSource @compiled/react */
import * as React from 'react';
import { css } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import type { KnowledgeArticle } from 'state/ui/search';
import { SearchResultKb } from 'view/common/result-kb';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import messages from './messages';

interface KbSuggestionFieldProps {
	articles: KnowledgeArticle[];
	connectedToForm?: boolean;
	portalId?: number;
}

export const KbSuggestionField: React.FunctionComponent<
	KbSuggestionFieldProps & WrappedComponentProps & WithAnalyticsEventsProps
> = ({ createAnalyticsEvent, articles, intl, connectedToForm, portalId }) => {
	React.useEffect(() => {
		createAnalyticsEvent &&
			createAnalyticsEvent({
				action: 'viewed',
				analyticsType: TRACK_EVENT_TYPE,
				actionSubjectId: `kbSuggestion${connectedToForm ? '-proforma' : ''}`,
				actionSubject: `kbSuggestionField${connectedToForm ? '-proforma' : ''}`,
				totalArticles: articles.length,
			}).fire();
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [createAnalyticsEvent, articles.length]);

	const articleList = !isEmpty(articles) && (
		<Container>
			{articles.map((article) => (
				<KbArticle
					articleUrl={article.articleUrl}
					sourceType={article.sourceType}
					key={article.id}
					elevated={false}
					title={article.name}
					openNewTab
					focusable
					isSuggestion
					portalId={typeof portalId === 'number' ? portalId.toString() : portalId}
					{...article}
				/>
			))}
		</Container>
	);

	return (
		<Root>
			<div data-test-id="request-field-kb-suggest-article">
				<div css={sectionHeadingWrapperStyles}>
					<Heading size="xxsmall" as="h2">
						{intl.formatMessage(messages.label)}
					</Heading>
				</div>
				{articleList}
			</div>
		</Root>
	);
};

export default withAnalyticsEvents()(injectIntl(KbSuggestionField));

const sectionHeadingWrapperStyles = css({
	marginBlockStart: token('space.200', '16px'),
	paddingBlockStart: token('space.025', '2px'),
	paddingBlockEnd: token('space.050', '4px'),
	color: token('color.text.subtle', '#505258'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&& h2': { color: 'inherit' },
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Root = styled.div`
	width: 100%;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Container = styled.div<{ isSmartSnippetsEnabled?: boolean }>`
	padding: ${token('space.200', '16px')};
	background-color: ${token('color.background.neutral', '#FAFBFC')};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const KbArticle = styled(SearchResultKb)`
	transition: box-shadow 0.2s ease-in-out;
`;
